import { useCustomReducer } from '../../../../../base/customHooks'
import IPButton from '../../../../../components/IPButton'
import { useMemo, useState } from 'react'
import '../../AccountSummary/AccountSummary.scss'
import Swal from 'sweetalert2'
import InputCampaign from '../../../../../components/InputCampaign'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import { exportToXLSXVDF, exportToXLSXVDFMultipleColumns, formatCurrencyTemlik, formatDate } from '../../../../../base/utils/dataHelper'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPExcelButton from '../../../../../components/IPExcelButton'
import Table from '../../../../../components/Table'
import { GetVodafoneTemlikRequest, GetVodafoneTemlikResponse } from '../models/model'
import { GetTemlikSummaryListApi } from '../services/getTemlikSummaryList'
import moment from 'moment'
import { access } from 'fs'

interface IVodafoneTemlikSummaryState {
  imei?: string
  shopCode?: string
  bayiKodu?: string
  periodStart?: string
  periodEnd?: string
  belgeTuruOptions: ISelectOption[]
  selectedBelgeTuru?: ISelectOption[]
}

const defaultBelgeTuruOptions: ISelectOption[] = [
  { value: 'A1', label: 'Bayi Risk Virmanı (A1)' },
  { value: 'A2', label: 'Bayi Risk VRM - Fark (A2)' },
  { value: 'A9', label: 'Bayi Risk VRM - Temlik (A9)' },
  { value: 'A0', label: 'Bayi Virmanı Ters İşlem (A0)' },
  { value: 'A4', label: 'Bayi Temlik Virmanı (A4)' },
  { value: 'A3', label: 'PGD - Datagate Fatura (A3)' },
  { value: 'B3', label: 'SFT - VDF Fatura (B3)' },
  { value: 'A6', label: 'PGD - DGT Payı (A6)' },
]

const ALL_BELGE_TURU_VALUE = '__ALL_BELGE_TURU__'

const getBelgeTuruLabel = (belgeTuru: string) => {
  const option = defaultBelgeTuruOptions.find(item => item.value.toString() === belgeTuru?.toString())
  return option?.label ?? belgeTuru
}

const mergeBelgeTuruOptions = (currentOptions: ISelectOption[], selectedOptions?: ISelectOption[]): ISelectOption[] => {
  const allOptions = [...currentOptions]

    ; (selectedOptions ?? []).forEach(option => {
      if (!allOptions.find(item => item.value.toString() === option.value.toString())) {
        allOptions.push(option)
      }
    })

  return allOptions
}

function VodafoneTemlikSummary(props: BaseComponentWithContextProps) {
  const getInitialState = (): IVodafoneTemlikSummaryState => {
    return {
      belgeTuruOptions: defaultBelgeTuruOptions,
      selectedBelgeTuru: defaultBelgeTuruOptions,
    }
  }

  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<IVodafoneTemlikSummaryState>(intialState)
  const [temlikData, setTemlikData] = useState<GetVodafoneTemlikResponse[]>([])

  const [startDate, setStartDate] = useState<string | undefined>(undefined)
  const [endDate, setEndDate] = useState<string | undefined>(undefined)
  const [aktivasyonStartDate, setAktivasyonStartDate] = useState<string | undefined>(undefined)
  const [aktivasyonEndDate, setAktivasyonEndDate] = useState<string | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState(0)

  const belgeTuruSelectOptions = useMemo<ISelectOption[]>(
    () => [
      {
        value: ALL_BELGE_TURU_VALUE,
        label: <span style={{ color: '#007bff', fontWeight: 600 }}>Tümünü Seç</span>,
      },
      ...state.belgeTuruOptions,
    ],
    [state.belgeTuruOptions]
  )

  const hasRangeMismatch = (start?: string, end?: string) => {
    return (!!start && !end) || (!start && !!end)
  }

  const onBelgeTuruChange = (options: ISelectOption[]) => {
    if (!options || options.length === 0) {
      setState({ selectedBelgeTuru: [] })
      return
    }

    const normalizedOptions = options.filter(item => item.value.toString() !== ALL_BELGE_TURU_VALUE)
    const mergedOptions = mergeBelgeTuruOptions(state.belgeTuruOptions, normalizedOptions)

    setState({ belgeTuruOptions: mergedOptions })

    const hasAllSelected = options.some(item => item.value.toString() === ALL_BELGE_TURU_VALUE)
    if (hasAllSelected) {
      setState({ selectedBelgeTuru: mergedOptions })
      return
    }

    setState({ selectedBelgeTuru: normalizedOptions })
  }

  const getTemlikDetailList = async () => {
    if (!state.selectedBelgeTuru || state.selectedBelgeTuru.length === 0) {
      return Swal.fire({
        icon: 'error',
        title: 'Belge Türü seçimi zorunludur.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }


    const req: GetVodafoneTemlikRequest = {
      Imei: state.imei?.trim() ?? '',
      ShopCode: state.shopCode?.trim() ?? '',
      BelgeTuru: state.selectedBelgeTuru.map(item => item.value.toString()),
      AktivasyonStartDate: aktivasyonStartDate ? formatDate(aktivasyonStartDate as any) : '',
      AktivasyonEndDate: aktivasyonEndDate ? formatDate(aktivasyonEndDate as any) : '',
      PeriodStart: state.periodStart?.trim() ?? '',
      PeriodEnd: state.periodEnd?.trim() ?? '',
    }

    props.showLoading()
    const api: GetTemlikSummaryListApi = new GetTemlikSummaryListApi({})

    api
      .getSummaryList(req)
      .then(result => {
        if (result) {
          setTemlikData(result)
          if (result.length === 0) {
            Swal.fire({
              icon: 'warning',
              title: 'Temlik özet verisi bulunamadı',
              showConfirmButton: true,
              allowOutsideClick: false,
            })
          }
          props.hideLoading()
        } else {
          props.hideLoading()
        }
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: err?.description ?? 'Temlik özet verisi alınırken hata oluştu.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  const columnDefinitions = [
    { Header: 'IMEI', accessor: 'imei' },
    { Header: 'Aktivasyon Tarihi', accessor: 'aktivasyonTarihi' },
    { Header: 'VDF Bayi Kodu', accessor: 'vdfBayiKodu' },
    { Header: 'Alt Bayi Kodu', accessor: 'altBayiKod' },
    { Header: 'Marka', accessor: 'marka' },
    { Header: 'Model', accessor: 'model' },
    { Header: 'EAN Kodu', accessor: 'eanKodu' },
    { Header: 'Ürün Kodu', accessor: 'urunKodu' },
    { Header: 'VDF Ürün Kodu', accessor: 'vdfUrunKodu' },
    { Header: 'Ürün Açıklaması', accessor: 'urunAciklamasi' },
    { Header: 'Dönem', accessor: 'donem' },
    { Header: 'A1 Bayi Risk Virmanı Tutar', accessor: 'a1' },
    { Header: 'A0 Bayi Ters İşlem Tutar', accessor: 'a0' },
    { Header: 'A2 Bayi Risk Virmanı Fark Tutar', accessor: 'a2' },
    { Header: 'A9 Bayi Risk Virmanı Temlik Tutar', accessor: 'a9' },
    { Header: 'Toplam Risk Virmanı', accessor: 'toplam' },
    { Header: 'Temlik Bayi Alış Fiyatı', accessor: 'temlikTutar' },
    { Header: 'Fiyat Farkı', accessor: 'fark' },
    { Header: 'A4 Bayi Temlik Virmanı Tutar', accessor: 'a4' },
    { Header: 'B3 Satış Faturası Tutar', accessor: 'b3' },
    { Header: 'A6 PGD Datagate Payı Tutar', accessor: 'a6' },
    { Header: 'A3 PGD Datagate Fatura Tutar', accessor: 'a3' },
  ]

  const tableColumns = useMemo(() => columnDefinitions, [])

  const tableDataAndTotal = useMemo(() => {
    const tableData = temlikData.map(item => {
      return {
        imei: item.IMEI,
        aktivasyonTarihi: item.AKTIVASYON_TARIHI ? moment(item.AKTIVASYON_TARIHI).format('DD.MM.YYYY') : '',
        vdfBayiKodu: item.VDF_BAYI_KODU,
        altBayiKod: item.VDF_ALT_BAYI,
        bayiKodu: item.BAYI_KODU,
        donem: item.DONEM,
        marka: item.MARKA,
        model: item.MODEL,
        eanKodu: item.EAN_KODU,
        urunKodu: item.URUN_KODU,
        vdfUrunKodu: item.VDF_URUN_KODU,
        urunAciklamasi: item.URUN_ACIKLAMASI,
        temlikTutar: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.TEMLIK_TUTAR)}</div>,
        toplam: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.TOPLAM)}</div>,
        fark: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.FARK)}</div>,
        a1: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.A1_BAYI_RISK_VIRMANI_TOPLAM)}</div>,
        a0: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(-item.A0_BAYI_TERS_ISLEM_TOPLAM)}</div>,
        a2: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(-item.A2_BAYI_RISK_VIRMANI_FARK_TOPLAM)}</div>,
        a9: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.A9_BAYI_RISK_VIRMANI_TEMLIK_TOPLAM)}</div>,
        a4: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.A4_BAYI_TEMLIK_VIRMANI_TOPLAM)}</div>,
        b3: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.B3_SATIS_FATURASI_TOPLAM)}</div>,
        a3: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.A3_PGD_DATAGATE_FATURA_TOPLAM)}</div>,
        a6: <div style={{ textAlign: 'right' }}>{formatCurrencyTemlik(item.A6_PGD_DATAGATE_PAYI_TOPLAM)}</div>,
      }
    })

    return {
      tableData,
    }
  }, [temlikData])

  const excelButton = async () => {
    try {
      if (!temlikData || temlikData.length === 0) {
        return Swal.fire({
          icon: 'error',
          title: 'Excele atılacak veri bulunmamaktadır.',
          allowOutsideClick: true,
          backdrop: true,
        })
      }

      const data: any[] = temlikData.map(item => ({
        IMEI: item.IMEI,
        'AKTİVASYON TARİHİ': item.AKTIVASYON_TARIHI ? moment(item.AKTIVASYON_TARIHI).format('DD.MM.YYYY') : '',
        'VDF BAYİ KODU': item.VDF_BAYI_KODU,
        'ALT BAYİ KODU': item.VDF_ALT_BAYI,
        DÖNEM: item.DONEM,
        MARKA: item.MARKA,
        MODEL: item.MODEL,
        'EAN KODU': item.EAN_KODU,
        'ÜRÜN KODU': item.URUN_KODU,
        'VDF ÜRÜN KODU': item.VDF_URUN_KODU,
        'ÜRÜN AÇIKLAMASI': item.URUN_ACIKLAMASI,
        'A1 BAYİ RİSK VİRMANI TUTAR': formatCurrencyTemlik(item.A1_BAYI_RISK_VIRMANI_TOPLAM),
        'A0 BAYİ TERS İŞLEM TUTAR': formatCurrencyTemlik(-item.A0_BAYI_TERS_ISLEM_TOPLAM),
        'A2 BAYİ RİSK VİRMANI FARK TUTAR': formatCurrencyTemlik(-item.A2_BAYI_RISK_VIRMANI_FARK_TOPLAM),
        'A9 BAYİ RİSK VİRMANI TEMLİK TUTAR': formatCurrencyTemlik(item.A9_BAYI_RISK_VIRMANI_TEMLIK_TOPLAM),
        'TOPLAM RİSK VİRMANI': formatCurrencyTemlik(item.TOPLAM),
        'TEMLİK BAYİ ALIŞ FİYATI': formatCurrencyTemlik(item.TEMLIK_TUTAR),
        'FİYAT FARKI': formatCurrencyTemlik(item.FARK),
        'A4 BAYİ TEMLİK VİRMANI TUTAR': formatCurrencyTemlik(item.A4_BAYI_TEMLIK_VIRMANI_TOPLAM),
        'B3 SATIŞ FATURASI TUTAR': formatCurrencyTemlik(item.B3_SATIS_FATURASI_TOPLAM),
        'A3 PGD DATAGATE FATURA TUTAR': formatCurrencyTemlik(item.A3_PGD_DATAGATE_FATURA_TOPLAM),
        'A6 PGD DATAGATE PAYI TUTAR': formatCurrencyTemlik(item.A6_PGD_DATAGATE_PAYI_TOPLAM),
      }))

      exportToXLSXVDFMultipleColumns(data, 'Temlik Özet', 'L,M,N,O,P,Q,R,S,T,U,V')
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description ?? 'Excel aktarımında hata oluştu.',
        allowOutsideClick: true,
        backdrop: true,
      })
    }
  }

  return (
    <div className='Reports'>
      <div className='heading-text d-flex justify-content-between align-items-center'>
        <h4 style={{ textAlign: 'center' }}>Temlik Özet</h4>
        <div className='d-flex align-items-center'>
          <IPExcelButton onClick={excelButton} />
        </div>
      </div>

      <form>
        <div className='form-row' style={{ display: 'flex', justifyContent: 'center' }}>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='text'
              id='Imei'
              name='Imei'
              label='IMEI'
              value={state.imei}
              onChange={e => setState({ imei: e.target.value })}
              isActive
            />
          </div>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='text'
              id='ShopCode'
              name='ShopCode'
              label='Mağaza Kodu'
              value={state.shopCode}
              onChange={e => setState({ shopCode: e.target.value })}
              isActive
            />
          </div>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='text'
              id='PeriodStart'
              name='PeriodStart'
              label='Dönem Başlangıç'
              value={state.periodStart}
              onChange={e => setState({ periodStart: e.target.value })}
              isActive
            />
          </div>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='text'
              id='PeriodEnd'
              name='PeriodEnd'
              label='Dönem Bitiş'
              value={state.periodEnd}
              onChange={e => setState({ periodEnd: e.target.value })}
              isActive
            />
          </div>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='date'
              id='AktivasyonStartDate'
              name='AktivasyonStartDate'
              label='Aktivasyon Başlangıç'
              value={aktivasyonStartDate}
              onChange={e => setAktivasyonStartDate(e.target.value)}
              isActive
            />
          </div>
          <div className='form-group col-md-2'>
            <InputCampaign
              type='date'
              id='AktivasyonEndDate'
              name='AktivasyonEndDate'
              label='Aktivasyon Bitiş'
              value={aktivasyonEndDate}
              onChange={e => setAktivasyonEndDate(e.target.value)}
              isActive
            />
          </div>
        </div>

        <div className='form-row' style={{ display: 'flex', justifyContent: 'center' }}>
          <div className='form-group col-md-3' style={{ justifyContent: 'center', display: 'flex' }}>
            <IPButton
              text='Listele'
              className='button'
              onClick={() => getTemlikDetailList()}
              style={{
                backgroundColor: 'red',
                color: 'white',
                borderRadius: '10px',
                width: '150px',
                height: '50px',
              }}
            />
          </div>
        </div>

        <label style={{ color: 'red' }}>
          <b>Dönem</b> alanlarını <b>YYYYMM</b> formatında giriniz. Örnek: {moment(new Date()).format('YYYYMM')}
        </label>
      </form>

      <div className='m-4'>
        <Table
          columns={tableColumns}
          data={tableDataAndTotal.tableData}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          columnCount={16}
          backgroundColor='red'
          color='white'
        />
      </div>
    </div>
  )
}

export default componentWithContext(VodafoneTemlikSummary)
